import { useState, useEffect, useRef } from "react";
import "./App.css";
import ScheduleSidebar from "./components/ScheduleSidebar"; // 👈 Presentational (receives props)
import MatchupsSidebar from "./components/MatchupsSidebar";
import HeaderBar from "./components/HeaderBar";
import Footer from "./components/Footer";


const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";



// 🔹 Suggested queries shown under the input when it's empty
const SUGGESTED_QUERIES = [
  "Best value defenders this week",
  "Sort all players by projected points",
  "Who are the best captain options?",
];

/* -------------------- Chat helpers -------------------- */

// --- tiny helpers for client-scoped last_plan (per tab) ---
const getLastPlan = () => {
  try {
    return JSON.parse(sessionStorage.getItem("last_plan") || "null");
  } catch {
    return null;
  }
};
const setLastPlan = (plan) => {
  if (!plan) sessionStorage.removeItem("last_plan");
  else sessionStorage.setItem("last_plan", JSON.stringify(plan));
};
const isFirstRequest = () => {
  const key = "first_request_sent";
  const already = sessionStorage.getItem(key) === "true";
  if (already) return false;     // all calls after #1
  sessionStorage.setItem(key, "true");
  return true;                   // exactly the first call
};
// ----- table helpers -----
function dedupePreserve(arr) {
  const out = [];
  const seen = new Set();
  for (const x of arr) {
    if (x && !seen.has(x)) {
      out.push(x);
      seen.add(x);
    }
  }
  return out;
}

/**
 * Build a simple ASCII table from a fmt-json payload
 */
/**
 * Build a simple ASCII table from a fmt-json payload
 */
function buildAsciiTableFromFmtJson(payload, planTitle = null) {
  const headers = (payload?.headers || []).map(String);
  const rowsIn = Array.isArray(payload?.rows) ? payload.rows : [];

  if (!headers.length || !rowsIn.length) return "(no rows)";

  // Normalize rows
  const rows = rowsIn.map((r) => {
    if (Array.isArray(r)) return r.map((v) => (v == null ? "" : String(v)));
    return headers.map((h) => (r?.[h] == null ? "" : String(r[h])));
  });

  // Column widths
  const widths = headers.map((h, i) =>
    Math.max(h.length, ...rows.map((r) => (r[i] ? r[i].length : 0)))
  );

  const pad = (s, w) => s + " ".repeat(Math.max(0, w - s.length));

  const header =
    "| " + headers.map((h, i) => pad(h, widths[i])).join(" | ") + " |";
  const sep = "|-" + widths.map((w) => "-".repeat(w)).join("-|-") + "-|";

  const bodyLines = rows.map((r) => {
    const isBlank = r.every((v) => v.trim() === "");
    if (isBlank) return "";
    return "| " + r.map((v, j) => pad(v, widths[j])).join(" | ") + " |";
  });

  // ----- TITLE (top-left aligned) -----
  if (planTitle) {
    const tableWidth = header.length;
    const rule = "=".repeat(tableWidth);

    return [
      planTitle,   // flush-left title
      rule,        // full-width rule line
      header,
      sep,
      ...bodyLines,
    ].join("\n");
  }

  // ----- Fallback: no title -----
  return [header, sep, ...bodyLines].join("\n");
}



/* -------------------- App -------------------- */

function App() {
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const chatEndRef = useRef(null);
  const hasSentHello = useRef(false);


  // ⬇️ NEW: Schedule state managed in App
  const [fixtures, setFixtures] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [scheduleErr, setScheduleErr] = useState(null);
  const [currentGW, setCurrentGW] = useState(null);
  const [minutesAgo, setMinutesAgo] = useState(null);



  const [followupQuestions, setFollowupQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchFollowupQuestions = async (userInput) => {
    try {
      const res = await fetch(`${API_URL}/suggest_followup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userInput, last_plan: getLastPlan() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(data.suggestions)) {
        setFollowupQuestions(data.suggestions);
      } else {
        console.log('error1')
        setFollowupQuestions([]);
      }
    } catch {
      console.log('error2')
      setFollowupQuestions([]);
    }
  };



  // ⬇️ NEW: Fetch schedule once in App, pass to ScheduleSidebar

  useEffect(() => {
    const ac = new AbortController();
    setScheduleLoading(true);
    setScheduleErr(null);

    fetch(`${API_URL}/schedule`, { signal: ac.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const schedule = Array.isArray(data?.schedule) ? data.schedule : [];
        setFixtures(schedule);
        const gw = data?.meta?.gw ?? 12;
        const mins = data?.meta?.minutes_ago ?? 0;
        setCurrentGW(gw);
        setMinutesAgo(mins);
      })
      .catch((e) => {
        if (e.name !== "AbortError") setScheduleErr(e.message || String(e));
      })
      .finally(() => setScheduleLoading(false));

    return () => ac.abort();
  }, []);

  useEffect(() => {
    if (!hasSentHello.current) {
      sendQuery("Hi", { silent: true }); // 👈 send but don't render
      hasSentHello.current = true;
    }
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const streamText = (fullText) => {
    setMessages((prev) => [
      ...prev,
      { role: "assistant", type: "text", content: "" },
    ]);
    setTimeout(() => {
      let i = 0;
      const interval = setInterval(() => {
        setMessages((prevMessages) => {
          const updated = [...prevMessages];
          const last = updated[updated.length - 1];

          if (i >= fullText.length) {
            clearInterval(interval);
            return updated;
          }

          last.content += fullText[i];
          updated[updated.length - 1] = last;
          i++;
          return updated;
        });
      }, 10);
    }, 10);
  };

const renderAnswer = (answer, planTitle = null) => {
  // ✅ Combo payload: { table, explanation }
  if (
    answer &&
    typeof answer === "object" &&
    "table" in answer &&
    "explanation" in answer
  ) {
    // 1) Render the table first (list), passing planTitle through
    renderAnswer(answer.table, planTitle);

    // 2) Then render the explanation using the normal path (no title needed)
    renderAnswer(answer.explanation);
    return;
  }

  // 🔹 Plain string -> normal assistant text bubble (streamed)
  if (typeof answer === "string") {
    streamText(answer);
    return;
  }

  // 🔹 Object shapes: explanation object or fmt-json table
  if (answer && typeof answer === "object") {
    const version = String(answer.version || "");

    // Explanation object like { version: "explanation-1.0", text: "..." }
    if (version.startsWith("explanation-") && typeof answer.text === "string") {
      streamText(answer.text);
      return;
    }

    // fmt-json table object
    if (version.startsWith("fmt-json")) {
      const table = buildAsciiTableFromFmtJson(answer, planTitle);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", type: "table", content: table },
      ]);
      return;
    }

    // Fallback: show raw JSON as text
    streamText(JSON.stringify(answer, null, 2));
    return;
  }

  // Final fallback for null/undefined/other primitives
  streamText(String(answer ?? ""));
};


const sendQuery = async (userInput, { silent = false } = {}) => {
  if (!silent) {
    setMessages((prev) => [
      ...prev,
      { role: "user", type: "text", content: userInput },
    ]);
  }

  setIsLoading(true);
  let first_request = false;
  if (!hasSentHello.current) {
    first_request = true;
  }

  try {
    const res = await fetch(`${API_URL}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: userInput,
        last_plan: getLastPlan(),
        first_request,
      }),
    });

    if (res.status === 503) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          type: "text",
          content: "Server warming up... retrying shortly ⏳",
        },
      ]);
      setTimeout(() => sendQuery(userInput), 3000);
      return;
    }

    const data = await res.json().catch(() => ({}));
    setIsLoading(false);
    if (!res.ok) {
      const errText =
        data?.error ||
        data?.detail ||
        `[${res.status}] ${res.statusText || "Request failed"}`;
      setMessages((prev) => [
        ...prev,
        { role: "assistant", type: "text", content: `Error: ${errText}` },
      ]);
      return;
    }

    // 🔹 Update last_plan from server
    if ("last_plan" in data) {
      setLastPlan(data.last_plan);
    } else if ("last_effective_plan" in data) {
      setLastPlan(data.last_effective_plan);
    }

    // 🔹 Extract plan_title from server response
    const planTitle = data?.plan_title ?? null;

    // 🔹 New: handle table + explanation combo
    const rawAnswer = data?.answer ?? data?.response ?? "";
    renderAnswer(rawAnswer, planTitle);

    await fetchFollowupQuestions(userInput);
  } catch (err) {
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        type: "text",
        content: "[Network error fetching response]",
      },
    ]);
  } finally {
    setIsLoading(false);
  }
};


  const handleQuery = () => {
    if (query.trim()) {
      sendQuery(query.trim());
      setQuery("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleQuery();
  };

  // 🔹 Clicking a suggestion should submit that query immediately
  const handleSuggestionClick = (text) => {
    sendQuery(text);
    setQuery(""); // keep the box visually empty after click
  };
 const SoccerLoader = () => <div className="loaderBall" />;

  return (
    <>
      <HeaderBar gameweek={currentGW} minutesAgo={minutesAgo} />
      <div className="container">
        <div className="layout3">
          {/* new 3-column layout */}
          <aside className="sidebar left">
            {/* ⬇️ Pass schedule props (no fetching inside the component) */}
            <ScheduleSidebar
              fixtures={fixtures}
              loading={scheduleLoading}
              err={scheduleErr}
            />
          </aside>

          <main className="main">
            <div className="chatBox">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`message ${
                    msg.role === "user" ? "user" : "assistant"
                  }`}
                >
                  {msg.type === "table" ? <pre>{msg.content}</pre> : msg.content}
                </div>
              ))}
              {isLoading && (
                <div style={{padding: "1px" }}>
                  <SoccerLoader />
                </div>
              )}

              <div ref={chatEndRef} />
            </div>

          {/* 🔹 Suggested prompts: in the main column,
              directly above the input, below the last message */}
              {followupQuestions.length > 0  && (
                <div className="suggestedQueries">
                  {followupQuestions.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="suggestedQueryChip"
                      onClick={() => handleSuggestionClick(q)}
                    >
                      ✨{q}
                    </button>
                  ))}
                </div>
              )}

            <div className="inputArea">
              {/* 🔹 Suggested queries row.
                  Shown ONLY when the input is empty so it doesn't interfere
                  with whatever the user is typing. */}
              <div className="inputRow">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask your question..."
                  className="input"
                />
                <button onClick={handleQuery} className="button">
                  Send
                </button>
              </div>
            </div>
          </main>

          <aside className="sidebar right">
            {/* <MatchupsSidebar gameweek={12} /> */}
          </aside>
        </div>
      </div>
      <Footer />   {/* <--- Add this */}

    </>
  );
}

export default App;
